import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ShellClientService } from '../../shell-communication/shell-client.service';
import { TimestampsService } from '../../timestamps.service';
import { DetectMethodChanges } from '../../utils';
import {
   CollarsManualMarkInput,
   CollarsRetroLegInput,
   CollarsRetroPreviewDto,
   CollarsSyntheticExpiryDto,
   CommitRetroFills,
   PreviewRetroFills
} from '../../shell-communication/collars-operations';
import { OptionsChainService } from '../../option-chains.service';
import { TradingInstrument } from '../../trading-instruments/trading-instrument.class';
import { TradingInstrumentKind } from '../../trading-instruments/trading-instrument-kind.enum';
import { TradingInstrumentDisplayNameService } from '../../trading-instruments/trading-instrument-display-name.service';
import { OptionExpirationDescriptor } from '../../shell-communication/shell-dto-protocol';
import { makeOptionTicker, parseOptionTicker } from '../../options-common/options.model';
import { makeCollarsOptionDisplayName } from '../../utils';
import { LoggerService } from '../../logging/logger-factory.service';
import { Logger } from '../../logging/logger.interface';

const KIND_ASSET = 0;
const KIND_OPTION = 1;

// A Group the per-leg router may target (the clicked group's siblings under the same Combo).
export interface CollarsBackdateRoutingGroup {
   bucketId: string;
   name: string;
}

export interface CollarsBackdateConfig {
   terminalId: string;
   // Default target Group for new legs: the clicked Group, or null when opened from a COMBO row —
   // then every leg must pick its Target Group explicitly (owner ruling, vrequest 2026-06-10).
   groupBucketId?: string | null;
   groupName?: string;
   comboName?: string;
   portfolioName?: string;
   routingGroups?: CollarsBackdateRoutingGroup[];
}

// One leg of the backdated structure. Mirrors the retired "Add Position" blotter's leg composer
// (CR-20260610-001) minus the live reference quote (historical entry needs no live mid/last): option
// legs compose their code from the chain descriptor; every leg carries its OWN trade date/time —
// REQUIRED, per-leg valid-time (owner decision) — and its own target Group (same Combo).
interface BackdateLeg {
   id: number;
   kind: number;                            // KIND_ASSET | KIND_OPTION
   side: number;                            // 1 = Buy, -1 = Sell
   qty: number;
   avgPx: number;
   optionType: string;                      // 'Put' | 'Call' (option legs only)
   strike: number;                          // option legs only
   expiration: OptionExpirationDescriptor;  // option legs only (the descriptor the picker emits)
   tradeDate: string;                       // REQUIRED local wall-clock 'YYYY-MM-DDTHH:mm:ss'
   tradeTz: string;                         // IANA zone the wall-clock is read in
   targetGroupBucketId: string;             // per-leg routing (same Portfolio + Combo)
   // Expired-contract entry (retro-expired options): the chain lists only LIVE expirations, so a past
   // expiration is typed manually and the option code composes from a synthesized descriptor.
   useCustomExpiration?: boolean;
   customExpiration?: Date | string;        // the typed past expiration date
   customRoot?: string;                     // contract series root (@SPX vs @SPXW); asked only when the chain shows several
}

/**
 * The Backdate dialog — the multi-leg DATED-entry dialog (CR-20260610-001). Forward entry lives in
 * the Make Transaction dialog (date-less by construction); this dialog is the only place a trade time
 * is ever typed. The operator composes a structure on ONE underlying (≤1 asset leg + option legs;
 * index underlyings offer options only), each leg with its own qty/side/price/date+zone/target Group,
 * then PREVIEWS the combined consequences (a server dry run that writes nothing — sessions
 * re-attributed, positions moved, lifetime before/after, prices still needed), supplies any missing
 * prices, and COMMITS — every leg books atomically, or none does. Promise-based: show() resolves true
 * on commit, false on cancel.
 */
@Component({
   selector: 'ets-collars-backdate-dialog',
   templateUrl: './collars-backdate-dialog.component.html',
   styleUrls: ['./collars-backdate-dialog.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollarsBackdateDialogComponent {

   constructor(
      private readonly _changeDetector: ChangeDetectorRef,
      private readonly _shellClient: ShellClientService,
      private readonly _toastr: ToastrService,
      private readonly _timestamps: TimestampsService,
      private readonly _optionChains: OptionsChainService,
      private readonly _displayNames: TradingInstrumentDisplayNameService,
      loggerService: LoggerService
   ) {
      this._logger = loggerService.createLogger('CollarsBackdateDialog');
   }

   private readonly _logger: Logger;

   isVisible = false;
   busy = false;
   // The consequences POPUP (owner, round 10): opens when the preview result arrives; carries the slip
   // + the commit decision. Closing it discards the preview state.
   previewPopupVisible = false;

   readonly sides = [{ id: 1, text: 'Buy' }, { id: -1, text: 'Sell' }];
   readonly optionTypes = [{ id: 'Put', text: 'Put' }, { id: 'Call', text: 'Call' }];

   private _config: CollarsBackdateConfig;
   private _resolve: (committed: boolean) => void;
   private _legSeq = 0;

   // The composer (single underlying drives every option leg's chain).
   instrument: TradingInstrument = null;
   expirationsList: OptionExpirationDescriptor[] = [];
   legs: BackdateLeg[] = [];
   routingGroups: CollarsBackdateRoutingGroup[] = [];

   preview: CollarsRetroPreviewDto = null;
   // Operator-supplied prices for the holes the preview reported, keyed by needKey().
   priceInputs: { [key: string]: number } = {};
   // Settlement-level overrides for synthesized retro expirations, keyed by expiryKey(). Pre-filled
   // with the fetched level after each preview; a CHANGED value commits as a manual mark for the
   // UNDERLYING at the settlement instant (the synthesis reads marks before the fetch — operator wins).
   expiryPriceInputs: { [key: string]: number } = {};

   get portfolioName(): string { return this._config ? (this._config.portfolioName || '') : ''; }
   get comboName(): string { return this._config ? (this._config.comboName || '') : ''; }
   get groupName(): string { return this._config ? (this._config.groupName || '') : ''; }

   @DetectMethodChanges()
   show(config: CollarsBackdateConfig): Promise<boolean> {
      this._config = config;
      this.instrument = null;
      this.expirationsList = [];
      this.legs = [];
      this.routingGroups = (config.routingGroups && config.routingGroups.length > 0)
         ? config.routingGroups.slice()
         : (config.groupBucketId ? [{ bucketId: config.groupBucketId, name: config.groupName || 'Group' }] : []);
      this.preview = null;
      this.priceInputs = {};
      this.expiryPriceInputs = {};
      this.previewPopupVisible = false;
      this.busy = false;
      this.isVisible = true;
      return new Promise<boolean>(resolve => { this._resolve = resolve; });
   }

   @DetectMethodChanges()
   onHidden() {
      this.isVisible = false;
      if (this._resolve) {
         this._resolve(false);
         this._resolve = null;
      }
   }

   @DetectMethodChanges()
   onCancel() {
      this.isVisible = false;
      if (this._resolve) {
         this._resolve(false);
         this._resolve = null;
      }
   }

   // ---- Leg composition (transplanted from the retired blotter, minus the live quote column) ----

   // Symbol picker emits a TradingInstrument; load its option chain for the expiration pickers.
   async onSymbolSelected(instrument: TradingInstrument): Promise<void> {
      this.instrument = instrument;
      this.expirationsList = [];
      if (instrument) {
         try {
            const chain = await this._optionChains.getChain(instrument.ticker);
            this.expirationsList = (chain && chain.expirations) ? chain.expirations.slice() : [];
         } catch (e) {
            this._toastr.error('Failed to Load Expirations');
         }
      }
      this.preview = null;
      this.priceInputs = {};
      this.expiryPriceInputs = {};
      this._changeDetector.detectChanges();
   }

   isOption(leg: BackdateLeg): boolean {
      return leg.kind === KIND_OPTION;
   }

   // Indexes (e.g. SPX) are not tradable as an asset leg — only options on them. At most one asset leg.
   get canAddAsset(): boolean {
      const inst = this.instrument;
      if (inst && inst.kind === TradingInstrumentKind.Index) { return false; }
      return this.assetLegs.length === 0;
   }

   // Render order: assets first, then a divider, then options (design grouping; independent of
   // insertion order). Both getters re-run per change-detection; *ngFor trackBy keeps control identity.
   get assetLegs(): BackdateLeg[] {
      return this.legs.filter(l => l.kind === KIND_ASSET);
   }

   get optionLegs(): BackdateLeg[] {
      return this.legs.filter(l => l.kind === KIND_OPTION);
   }

   trackLeg(_index: number, leg: BackdateLeg): number {
      return leg.id;
   }

   @DetectMethodChanges()
   addAsset(): void {
      const last = this.legs.length > 0 ? this.legs[this.legs.length - 1] : null;
      this.legs.push({
         id: ++this._legSeq,
         kind: KIND_ASSET,
         side: 1,
         qty: null,
         avgPx: null,
         optionType: null,
         strike: null,
         expiration: null,
         // Structures usually execute as one package — seed the date/zone from the previous leg.
         tradeDate: last ? last.tradeDate : '',
         tradeTz: last ? last.tradeTz : this._timestamps.getDefaultTimezone('UTC'),
         // No default from a COMBO-level open — the operator must pick (validated on preview/commit).
         targetGroupBucketId: this._config.groupBucketId || null
      });
      this.preview = null;
      this.priceInputs = {};
      this.expiryPriceInputs = {};
   }

   @DetectMethodChanges()
   addOption(): void {
      const opts = this.optionLegs;
      const last = opts.length > 0 ? opts[opts.length - 1] : null;
      const lastAny = this.legs.length > 0 ? this.legs[this.legs.length - 1] : null;
      this.legs.push({
         id: ++this._legSeq,
         kind: KIND_OPTION,
         side: 1,
         qty: null,
         avgPx: null,
         optionType: last ? last.optionType : 'Put',
         strike: last ? last.strike : null,
         expiration: last ? last.expiration : null,
         tradeDate: lastAny ? lastAny.tradeDate : '',
         tradeTz: lastAny ? lastAny.tradeTz : this._timestamps.getDefaultTimezone('UTC'),
         targetGroupBucketId: last ? last.targetGroupBucketId : (this._config.groupBucketId || null),
         // Expired-contract entry inherits like the rest — packages of expired legs compose in one flow.
         useCustomExpiration: last ? last.useCustomExpiration : false,
         customExpiration: last ? last.customExpiration : null,
         customRoot: last ? last.customRoot : null
      });
      this.preview = null;
      this.priceInputs = {};
      this.expiryPriceInputs = {};
   }

   @DetectMethodChanges()
   removeLeg(id: number): void {
      this.legs = this.legs.filter(l => l.id !== id);
      this.preview = null;
      this.priceInputs = {};
      this.expiryPriceInputs = {};
   }

   // Any leg edit invalidates the previewed consequences (the old preview no longer applies) — the
   // slip auto-collapses (vrequest 2026-06-10).
   @DetectMethodChanges()
   onLegChanged(): void {
      this.preview = null;
      this.priceInputs = {};
      this.expiryPriceInputs = {};
   }

   // Close = decline: the preview state dies with the popup; the next Preview recomputes fresh.
   @DetectMethodChanges()
   onPreviewPopupClose(): void {
      this.previewPopupVisible = false;
   }

   @DetectMethodChanges()
   onPreviewPopupHidden(): void {
      this.previewPopupVisible = false;
      this.preview = null;
      this.priceInputs = {};
      this.expiryPriceInputs = {};
   }

   // ---- Preview / Commit (the existing informed-confirm machinery, now batch ops) ----

   async onPreview(): Promise<void> {
      if (this.busy) { return; }
      const legs = this.buildLegInputs();
      if (!legs) { return; }

      this.busy = true;
      this._changeDetector.detectChanges();
      try {
         this._logger.debug(`Collars backdate preview -> legs=${legs.length} terminal=${this._config.terminalId}`);
         const dto = await this._shellClient.processQuery<CollarsRetroPreviewDto>(
            new PreviewRetroFills(this._config.terminalId, legs));
         this.preview = dto;
         this.priceInputs = {};
         // Pre-fill the settlement override inputs with the derived levels — editing one books a
         // manual mark for the underlying at the settle instant on commit.
         this.expiryPriceInputs = {};
         ((dto && dto.syntheticExpiries) || []).forEach(x => {
            this.expiryPriceInputs[this.expiryKey(x)] = x.settlementPrice;
         });
         // The result is in — raise the consequences popup (spinner stops in finally).
         this.previewPopupVisible = true;
         this._logger.debug(`Collars backdate preview ok: isRetro=${dto && dto.isRetro} priceNeeds=${dto && dto.priceNeeds ? dto.priceNeeds.length : 0} positionChanges=${dto && dto.positionChanges ? dto.positionChanges.length : 0} sessionChanges=${dto && dto.sessionChanges ? dto.sessionChanges.length : 0} blocked=${dto ? dto.blockedReason : null}`);
      } catch (e) {
         this._logger.error('Collars backdate preview failed', { error: e.stack || e });
         this._toastr.error('Preview Failed');
         this.preview = null;
      } finally {
         this.busy = false;
         this._changeDetector.detectChanges();
      }
   }

   async onCommit(): Promise<void> {
      if (!this.canCommit()) { return; }
      const legs = this.buildLegInputs();
      if (!legs) { return; }

      const marks: CollarsManualMarkInput[] = (this.preview.priceNeeds || []).map(n => ({
         tradingInstrumentCode: n.tradingInstrumentCode,
         groupBucketId: n.groupBucketId,
         price: this.priceInputs[this.needKey(n)] || 0,
         asOfUtc: typeof n.asOfUtc === 'string' ? n.asOfUtc : (n.asOfUtc as Date).toISOString()
      }));

      // Settlement-level overrides for synthesized retro expirations: a CHANGED value books as a manual
      // mark for the UNDERLYING at the settlement instant — the synthesis reads marks before the fetched
      // level, so the operator's number wins. Unchanged values send nothing (no log pollution).
      (this.preview.syntheticExpiries || []).forEach(x => {
         const v = this.expiryPriceInputs[this.expiryKey(x)];
         if (!(v > 0) || Math.abs(v - x.settlementPrice) < 1e-9) { return; }
         marks.push({
            tradingInstrumentCode: x.underlyingCode,
            groupBucketId: x.groupBucketId,
            price: v,
            asOfUtc: typeof x.settleTimeUtc === 'string' ? x.settleTimeUtc : (x.settleTimeUtc as Date).toISOString()
         });
      });

      this.busy = true;
      this._changeDetector.detectChanges();
      this._logger.debug(`Collars backdate commit -> legs=${legs.length} marks=${marks.length} terminal=${this._config.terminalId}`);
      try {
         await this._shellClient.processCommand(new CommitRetroFills(this._config.terminalId, legs, marks));
         this._logger.info(`Collars backdate commit ok: legs=${legs.length} marks=${marks.length}`);
         this._toastr.success(legs.length > 1 ? 'Retro-Fills Committed' : 'Retro-Fill Committed');
         this.previewPopupVisible = false;
         this.isVisible = false;
         if (this._resolve) { this._resolve(true); this._resolve = null; }
      } catch (e) {
         // Most often the backend's CollarsPriceRequiredException (a price hole) — log the real reason
         // so a rejected commit is diagnosable. The batch is atomic: nothing was booked.
         this._logger.error('Collars backdate commit failed (e.g. price required / recompute rejected; batch is atomic — nothing booked)', { error: e.stack || e });
         this._toastr.error('Commit Failed — Nothing Was Booked');
      } finally {
         this.busy = false;
         this._changeDetector.detectChanges();
      }
   }

   // ---- view helpers ----

   needKey(n: { tradingInstrumentCode: string; groupBucketId: string; asOfUtc: string | Date }): string {
      const asOf = typeof n.asOfUtc === 'string' ? n.asOfUtc : (n.asOfUtc ? (n.asOfUtc as Date).toISOString() : '');
      return n.tradingInstrumentCode + '|' + n.groupBucketId + '|' + asOf;
   }

   // Per-keystroke price commits (the boxes set valueChangeEvent="input"), decorated like every other
   // editor in this dialog: canCommit() reads priceInputs — the bare [(value)] + DX 'change' commit
   // left the Commit button disabled until blur, with no handler-driven repaint behind it.
   @DetectMethodChanges()
   onPriceNeedChange(n: { tradingInstrumentCode: string; groupBucketId: string; asOfUtc: string | Date }, value: number): void {
      this.priceInputs[this.needKey(n)] = value;
   }

   @DetectMethodChanges()
   onExpiryPriceChange(x: CollarsSyntheticExpiryDto, value: number): void {
      this.expiryPriceInputs[this.expiryKey(x)] = value;
   }

   canCommit(): boolean {
      if (this.busy || !this.preview || !this.preview.isRetro || this.preview.blockedReason) { return false; }
      const needs = this.preview.priceNeeds || [];
      return needs.every(n => (this.priceInputs[this.needKey(n)] || 0) > 0);
   }

   // The preview's ONE summary table (vrequest 2026-06-10): Change | Instrument | Net. Three net
   // effects only — a backdate creates a position, closes one, or adjusts one; everything deeper is
   // reviewable in the Ledger itself.
   changeOf(p: { isNew: boolean; netBefore: number; netAfter: number }): string {
      if (p.isNew) { return 'New'; }
      if (p.netAfter === 0 && p.netBefore !== 0) { return 'Closed'; }
      return 'Adjusted';
   }

   changeClass(p: { isNew: boolean; netBefore: number; netAfter: number }): string {
      const c = this.changeOf(p);
      return c === 'New' ? 'chg-new' : (c === 'Closed' ? 'chg-closed' : 'chg-adj');
   }

   // Human-facing instrument name, NEVER the raw ticker (standing rule; exemplar dispName() in
   // collars.component.ts): an option code resolves via parseOptionTicker -> makeCollarsOptionDisplayName
   // (underlying friendly name from the display-name service); a stock/futures code via
   // getDisplayNameForTicker. Discriminate by parse validity, not the '@' prefix.
   dispName(code: string): string {
      if (!code) { return ''; }
      const t = parseOptionTicker(code);
      if (t) {
         const ulName = this._displayNames.getDisplayNameForTicker(t.underlying);
         return makeCollarsOptionDisplayName(t, ulName);
      }
      return this._displayNames.getDisplayNameForTicker(code) || code;
   }

   // ---- retro-expired options: expired-contract entry + settlement override ----

   @DetectMethodChanges()
   toggleCustomExpiration(leg: BackdateLeg): void {
      leg.useCustomExpiration = !leg.useCustomExpiration;
      if (leg.useCustomExpiration && !leg.customRoot) {
         leg.customRoot = this.defaultRoot;
      }
      this.preview = null;
      this.priceInputs = {};
      this.expiryPriceInputs = {};
   }

   // Distinct contract-series roots the live chain shows (e.g. ['@SPX', '@SPXW']). The typed-date entry
   // asks for the root only when there are several — the user declares WHICH series they traded.
   get chainRoots(): string[] {
      return this.expirationsList
         .map(x => x.expirationTicker)
         .filter((v, i, a) => !!v && a.indexOf(v) === i);
   }

   get defaultRoot(): string {
      const roots = this.chainRoots;
      if (roots.length > 0) { return roots[0]; }
      return this.instrument ? '@' + this.instrument.ticker : '';
   }

   // Composer badge only — the backend derives the real settle boundary (expiration 4:20 PM ET).
   isExpiredLeg(leg: BackdateLeg): boolean {
      if (leg.kind !== KIND_OPTION) { return false; }
      const raw = leg.useCustomExpiration
         ? leg.customExpiration
         : (leg.expiration ? leg.expiration.optionExpirationDate : null);
      if (!raw) { return false; }
      // Date-only strings parse to LOCAL midnight ('T00:00:00'), not UTC — else the badge is a day off
      // in negative-offset zones.
      const s = typeof raw === 'string' && raw.length === 10 ? raw + 'T00:00:00' : raw;
      const exp = new Date(s as any);
      if (isNaN(exp.getTime())) { return false; }
      exp.setHours(23, 59, 59, 999);
      return exp.getTime() < Date.now();
   }

   expiryKey(x: CollarsSyntheticExpiryDto): string {
      const asOf = typeof x.settleTimeUtc === 'string'
         ? x.settleTimeUtc
         : (x.settleTimeUtc ? (x.settleTimeUtc as Date).toISOString() : '');
      return x.optionInstrumentCode + '|' + x.groupBucketId + '|' + asOf;
   }

   // Wire values = backend CollarsExpiryOutcome ordinals.
   outcomeLabel(outcome: number): string {
      switch (outcome) {
         case 0: return 'Worthless';
         case 1: return 'Assigned';
         case 2: return 'Exercised';
         case 3: return 'Cash Settled';
         default: return '?';
      }
   }

   // Outcome coloring speaks the slip's existing palette: red = value died, blue = became the
   // underlying, amber = settled to cash.
   outcomeClass(outcome: number): string {
      switch (outcome) {
         case 0: return 'out-worthless';
         case 1: return 'out-assigned';
         case 2: return 'out-exercised';
         case 3: return 'out-cash';
         default: return '';
      }
   }

   // The chain lists only LIVE expirations — an EXPIRED contract composes from a synthesized descriptor:
   // the typed past date + the declared series root (ticker format '@root UL Type Strike yyyy-MM-dd';
   // backend parses the date with DateTime.TryParse and synthesizes the instrument from the code).
   private customExpirationDescriptor(leg: BackdateLeg): OptionExpirationDescriptor {
      return {
         expirationTicker: leg.customRoot || this.defaultRoot,
         underlyingTicker: this.instrument.ticker,
         optionExpirationDate: CollarsBackdateDialogComponent.toYmd(leg.customExpiration)
      } as OptionExpirationDescriptor;
   }

   private static toYmd(v: Date | string): string {
      // The platform picker (mode="date") emits 'YYYY-MM-DD' already — slice, never round-trip through
      // Date: new Date('YYYY-MM-DD') parses as UTC midnight and shifts a day in negative-offset zones.
      if (typeof v === 'string') { return v.slice(0, 10); }
      const m = ('0' + (v.getMonth() + 1)).slice(-2);
      const day = ('0' + v.getDate()).slice(-2);
      return v.getFullYear() + '-' + m + '-' + day;
   }

   // ---- leg validation + wire mapping ----

   private buildLegInputs(): CollarsRetroLegInput[] | null {
      if (!this.instrument) { this._toastr.warning('Pick the Underlying'); return null; }
      if (!this.legs.length) { this._toastr.warning('Add at Least One Leg'); return null; }

      const defaultTz = this._timestamps.getDefaultTimezone('UTC');
      const inputs: CollarsRetroLegInput[] = [];
      for (const leg of this.legs) {
         if (!leg.qty || leg.qty <= 0) { this._toastr.warning('Each Leg Needs a Positive Quantity'); return null; }
         if (!leg.avgPx || leg.avgPx <= 0) { this._toastr.warning('Each Leg Needs an Average Price'); return null; }
         if (!leg.tradeDate) { this._toastr.warning('Each Leg Needs Its Trade Date and Time'); return null; }
         if (!leg.targetGroupBucketId) { this._toastr.warning('Each Leg Needs a Target Group'); return null; }
         if (leg.kind === KIND_OPTION) {
            if (leg.useCustomExpiration) {
               if (!leg.customExpiration) { this._toastr.warning('Each expired option leg needs its expiration date'); return null; }
            } else if (!leg.expiration) { this._toastr.warning('Each option leg needs an expiration'); return null; }
            if (!leg.optionType) { this._toastr.warning('Each option leg needs a type'); return null; }
            if (!leg.strike || leg.strike <= 0) { this._toastr.warning('Each option leg needs a strike'); return null; }
         }

         inputs.push({
            groupBucketId: leg.targetGroupBucketId,
            kind: leg.kind,
            // Fully-composed code: option = makeOptionTicker (preserves the weekly/monthly root);
            // asset = the underlying instrument ticker.
            tradingInstrumentCode: leg.kind === KIND_OPTION
               ? makeOptionTicker(leg.useCustomExpiration ? this.customExpirationDescriptor(leg) : leg.expiration,
                  leg.optionType, leg.strike)
               : this.instrument.ticker,
            qty: leg.qty,
            side: leg.side,
            avgPx: leg.avgPx,
            // The picked wall-clock read in the leg's zone, sent as a UTC ISO instant (REQUIRED).
            tradeTimeUtc: this._timestamps.toUtcIsoFromZonedWallClock(leg.tradeDate, leg.tradeTz || defaultTz)
         });
      }
      return inputs;
   }
}
